import { defineStore } from "pinia";
import { computed, ref } from "vue";

import type { Master } from "@/entities/master";
import { masterApi, masterFullName } from "@/entities/master";
import type { Service } from "@/entities/service";
import { serviceApi } from "@/entities/service";
import type { ServiceCategory } from "@/entities/service-category";
import { serviceCategoryApi } from "@/entities/service-category";
import { useSessionStore } from "@/entities/session";
import type { AppError } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { toast } from "@/shared/lib";
import { getInitials } from "@/shared/ui/avatar";

import * as collapseStorage from "./collapseStorage";
import type { ServiceErrorKey } from "./mapServiceError";
import { mapServiceError } from "./mapServiceError";

/** Стан завантаження списку (spec §6). */
export type ListStatus = "idle" | "loading" | "ready" | "error";

/** Режим збереженого рядка — per-row стейт-машина (spec §5). */
export type SavedRowMode =
  | "idle"
  | "editing"
  | "pendingRename"
  | "deleteConfirm"
  | "pendingDelete"
  | "blocked";

/** Збережений рядок: канонічний `service` із сервера + ефемерний UI-стан. */
export interface SavedRow {
  kind: "saved";
  service: Service;
  mode: SavedRowMode;
  /** Інлайн-помилка (422 rename / 409 delete-block). */
  errorKey?: ServiceErrorKey;
  /** Локальний буфер тексту під час `editing`/`pendingRename`. */
  editValue?: string;
  /** Буфер категорії під час `editing`/`pendingRename`. */
  editCategoryId?: string | null;
}

/** Оптимістичний рядок створення (temp-id) до reconcile на 201/error (spec §5.2). */
export interface PendingCreateRow {
  kind: "pendingCreate";
  tempId: string;
  name: string;
  /** null/undefined = без категорії. */
  categoryId?: string | null;
  mode: "pending" | "error";
  errorKey?: ServiceErrorKey;
}

export type Row = SavedRow | PendingCreateRow;

export interface Group {
  category: ServiceCategory | null;
  rows: Row[];
}

let tempSeq = 0;

function tr(key: string, named?: Record<string, unknown>): string {
  return named ? i18n.global.t(key, named) : i18n.global.t(key);
}

function toSavedRow(service: Service): SavedRow {
  return { kind: "saved", service, mode: "idle" };
}

/**
 * Стор каталогу послуг (spec §6) — server-authoritative список + ефемерні per-row
 * UI-стани. Setup-store як `entities/session` (Δ Vuex: `ref`/`computed`/функції,
 * без mutations). Живе у feature (S5): UI-стан рядків — екранний.
 *
 * Create — оптимістична pending-вставка (Excel-потік); rename/delete —
 * server-confirmed (показуємо «збереження…», застосовуємо лише на успіх).
 */
export const useServicesCatalogStore = defineStore("services-catalog", () => {
  const listStatus = ref<ListStatus>("idle");
  const rows = ref<Row[]>([]);
  const categories = ref<ServiceCategory[]>([]);
  /** Polite-озвучення success для скрін-рідера (spec §9), без toast-спаму. */
  const politeMessage = ref("");
  /** Яка група зараз має відкритий draft-ввід (null = uncategorized). */
  const activeDraft = ref<{ categoryId: string | null } | null>(null);
  /** Відкрита форма створення нової категорії. */
  const categoryDraftOpen = ref(false);
  /** Session-persisted collapse state (spec §4, D-1). Only named categories are ever toggled. */
  const collapsedCategoryIds = ref<Set<string>>(new Set());
  /** Ростер майстрів (приєднується non-blocking під час load). */
  const roster = ref<Master[]>([]);
  const rosterStatus = ref<"idle" | "loading" | "ready" | "error">("idle");

  let collapseHydratedFor: string | null = null;

  function commitCollapsed(next: Set<string>): void {
    collapsedCategoryIds.value = next;
    collapseStorage.persistCollapsedCategories(useSessionStore().company?.id, next);
  }

  function hydrateCollapse(): void {
    const companyId = useSessionStore().company?.id ?? null;
    if (!companyId || companyId === collapseHydratedFor) return;
    collapsedCategoryIds.value = collapseStorage.readCollapsedCategories(companyId);
    collapseHydratedFor = companyId;
  }

  function toggleCategoryCollapse(categoryId: string): void {
    const next = new Set(collapsedCategoryIds.value);
    if (next.has(categoryId)) {
      next.delete(categoryId);
    } else {
      next.add(categoryId);
    }
    commitCollapsed(next);
  }

  function isCategoryCollapsed(categoryId: string): boolean {
    return collapsedCategoryIds.value.has(categoryId);
  }

  function openDraft(categoryId: string | null): void {
    if (categoryId !== null && isCategoryCollapsed(categoryId)) {
      commitCollapsed(new Set(
        [...collapsedCategoryIds.value].filter((id) => id !== categoryId),
      ));
    }
    activeDraft.value = { categoryId };
  }

  function closeDraft(): void {
    activeDraft.value = null;
  }

  function openCategoryDraft(): void {
    categoryDraftOpen.value = true;
  }

  function closeCategoryDraft(): void {
    categoryDraftOpen.value = false;
  }

  function isDraftOpen(categoryId: string | null): boolean {
    if (activeDraft.value === null) return false;
    return activeDraft.value.categoryId === categoryId;
  }

  const isEmpty = computed(
    () => listStatus.value === "ready" && rows.value.length === 0,
  );
  /** Чи є вже бодай одна збережена послуга (для placeholder draft-рядка). */
  const hasSavedRows = computed(() =>
    rows.value.some((r) => r.kind === "saved"),
  );

  /** True коли каталог порожній після першого завантаження (немає ні послуг, ні категорій). */
  const isFirstRun = computed(
    () =>
      listStatus.value === "ready" &&
      categories.value.length === 0 &&
      rows.value.length === 0,
  );

  /** Опції для select «категорія»: перша — «Без категорії», далі — список категорій. */
  const categoryOptions = computed(() => [
    { value: "__none__", label: tr("services.categories.uncategorized") },
    ...categories.value.map((c) => ({ value: c.id, label: c.name })),
  ]);

  /**
   * Згруповані рядки за категорією. Порядок: категорії у списку → null-група останньою.
   * Категорія без рядків — включається з порожнім масивом rows: [].
   */
  const groups = computed<Group[]>(() => {
    const result: Group[] = [];

    for (const cat of categories.value) {
      const catRows = rows.value.filter((r) => {
        if (r.kind === "saved") return r.service.categoryId === cat.id;
        if (r.kind === "pendingCreate") return r.categoryId === cat.id;
        return false;
      });
      result.push({ category: cat, rows: catRows });
    }

    const uncatRows = rows.value.filter((r) => {
      if (r.kind === "saved") return !r.service.categoryId;
      if (r.kind === "pendingCreate") return !r.categoryId;
      return true;
    });
    if (uncatRows.length > 0) {
      result.push({ category: null, rows: uncatRows });
    }

    return result;
  });

  function findSaved(id: string): SavedRow | undefined {
    return rows.value.find(
      (r): r is SavedRow => r.kind === "saved" && r.service.id === id,
    );
  }

  function findPending(tempId: string): PendingCreateRow | undefined {
    return rows.value.find(
      (r): r is PendingCreateRow =>
        r.kind === "pendingCreate" && r.tempId === tempId,
    );
  }

  function removeRow(predicate: (r: Row) => boolean): void {
    rows.value = rows.value.filter((r) => !predicate(r));
  }

  /** Майстри, призначені на послугу (join з roster). */
  function mastersForService(serviceId: string): Master[] {
    return roster.value.filter((m) => m.serviceIds.includes(serviceId));
  }

  /**
   * ≤3 initials + overflow count для avatar-stack у ServiceRow (spec §4.1).
   * Degrade: ростер не готовий → `{ initials: [], extra: assignedMasterCount }`.
   * Server-authoritative: `assignedMasterCount` — основний дискримінатор empty/filled.
   */
  function assignedPreview(serviceId: string): { initials: string[]; extra: number } {
    const row = rows.value.find(
      (r): r is SavedRow => r.kind === "saved" && r.service.id === serviceId,
    );
    const count = row?.service.assignedMasterCount ?? 0;

    if (rosterStatus.value !== "ready") {
      return { initials: [], extra: count };
    }

    const masters = mastersForService(serviceId);
    const shown = masters.slice(0, 3);
    const initials = shown.map((m) => getInitials(masterFullName(m)));
    const extra = Math.max(0, count - shown.length);
    return { initials, extra };
  }

  /**
   * Завантаження послуг і категорій паралельно. Послуги — критичний шлях
   * (failure → `listStatus = "error"`). Категорії — fire-and-forget: відповідь
   * застосовується коли прийде; збій ігнорується (порожній список). Це зберігає
   * backward-compat із компонентними тестами, що мокають лише `serviceApi`.
   * Ростер майстрів — теж fire-and-forget, ніколи не блокує `listStatus`.
   */
  async function load(): Promise<void> {
    hydrateCollapse();
    listStatus.value = "loading";
    rosterStatus.value = "loading";
    // Категорії: запускаємо паралельно, але не блокуємо готовність на них.
    void serviceCategoryApi.listCategories()
      .then((cats) => { categories.value = cats; })
      .catch(() => {});
    // Ростер: fire-and-forget, ніколи не блокує listStatus.
    void masterApi.listMasters()
      .then((masters) => { roster.value = masters; rosterStatus.value = "ready"; })
      .catch(() => { rosterStatus.value = "error"; });
    try {
      const services = await serviceApi.listServices();
      rows.value = services.map(toSavedRow);
      listStatus.value = "ready";
    } catch {
      listStatus.value = "error";
    }
  }

  /**
   * Потоковий create (spec §5.1/5.2): trim, no-op на порожнє, оптимістична вставка.
   * `categoryId` — другий необов'язковий параметр для backward-compat із старими тестами,
   * що викликають `create(name)` без категорії.
   */
  function create(rawName: string, categoryId?: string | null): void {
    const name = rawName.trim();
    if (!name) {
      return;
    }
    const tempId = `tmp-${++tempSeq}`;
    rows.value.push({ kind: "pendingCreate", tempId, name, categoryId: categoryId ?? null, mode: "pending" });
    void submitCreate(tempId, name, categoryId ?? null);
  }

  async function submitCreate(tempId: string, name: string, categoryId: string | null): Promise<void> {
    try {
      // Не передаємо categoryId коли він null — щоб API-виклик залишався сумісним
      // зі старими тестами, що перевіряють `createService("Назва")` без другого аргументу.
      const service = categoryId != null
        ? await serviceApi.createService(name, categoryId)
        : await serviceApi.createService(name);
      const row = findPending(tempId);
      if (row) {
        const idx = rows.value.indexOf(row);
        rows.value[idx] = toSavedRow(service);
      }
      politeMessage.value = tr("services.a11y.savedAnnounce", { name: service.name });
    } catch (e) {
      const mapped = mapServiceError(e as AppError, "create");
      const row = findPending(tempId);
      if (!row) {
        return;
      }
      row.mode = "error";
      row.errorKey = mapped.surface === "inline" ? mapped.i18nKey : undefined;
      if (mapped.surface === "toast") {
        toast.error(tr(mapped.i18nKey));
      }
    }
  }

  /** Повторити невдалий create-рядок (зберігши введену назву). */
  function retryCreate(tempId: string): void {
    const row = findPending(tempId);
    if (!row) {
      return;
    }
    row.mode = "pending";
    row.errorKey = undefined;
    void submitCreate(tempId, row.name, row.categoryId ?? null);
  }

  /** Прибрати error-рядок створення (ввід відкинуто користувачем). */
  function dismissCreate(tempId: string): void {
    removeRow((r) => r.kind === "pendingCreate" && r.tempId === tempId);
  }

  function beginRename(id: string): void {
    const row = findSaved(id);
    if (!row) {
      return;
    }
    row.mode = "editing";
    row.editValue = row.service.name;
    row.editCategoryId = row.service.categoryId ?? null;
    row.errorKey = undefined;
  }

  function cancelRename(id: string): void {
    const row = findSaved(id);
    if (!row) {
      return;
    }
    row.mode = "idle";
    row.editValue = undefined;
    row.editCategoryId = undefined;
    row.errorKey = undefined;
  }

  /**
   * Коміт перейменування/переміщення (spec §5.4).
   * No-op коли ім'я порожнє або коли обидва поля (name і categoryId) не змінились.
   * `categoryId` — необов'язковий: старі виклики з 2 аргументами залишаються сумісними
   * (no-op тільки по імені, як раніше).
   */
  async function commitRename(id: string, rawName: string, categoryId?: string | null): Promise<void> {
    const row = findSaved(id);
    if (!row || row.mode === "pendingRename") {
      return;
    }
    const name = rawName.trim();
    if (!name) {
      cancelRename(id);
      return;
    }
    const nameUnchanged = name === row.service.name;
    if (categoryId === undefined) {
      // Backward-compat: no-op тільки коли ім'я не змінилось.
      if (nameUnchanged) {
        cancelRename(id);
        return;
      }
    } else {
      // Новий шлях: no-op тільки коли обидва поля не змінились.
      const categoryUnchanged = categoryId === (row.service.categoryId ?? null);
      if (nameUnchanged && categoryUnchanged) {
        cancelRename(id);
        return;
      }
    }
    row.mode = "pendingRename";
    row.editValue = name;
    row.errorKey = undefined;
    try {
      const updated = await serviceApi.renameService(id, name, categoryId);
      row.service = updated;
      row.mode = "idle";
      row.editValue = undefined;
      row.editCategoryId = undefined;
      politeMessage.value = tr("services.a11y.savedAnnounce", { name: updated.name });
      toast.success(tr("services.a11y.savedAnnounce", { name: updated.name }));
    } catch (e) {
      const mapped = mapServiceError(e as AppError, "rename");
      if (mapped.removeRow) {
        removeRow((r) => r.kind === "saved" && r.service.id === id);
        toast.info(tr(mapped.i18nKey));
        return;
      }
      if (mapped.surface === "inline") {
        row.mode = "editing";
        row.errorKey = mapped.i18nKey;
      } else {
        row.mode = "editing";
        toast.error(tr(mapped.i18nKey));
      }
    }
  }

  function beginDelete(id: string): void {
    const row = findSaved(id);
    if (!row) {
      return;
    }
    row.mode = "deleteConfirm";
    row.errorKey = undefined;
  }

  function cancelDelete(id: string): void {
    const row = findSaved(id);
    if (!row) {
      return;
    }
    row.mode = "idle";
    row.errorKey = undefined;
  }

  /** Підтвердження видалення (spec §5.5): 204→прибрати, 409→block, 404→прибрати. */
  async function confirmDelete(id: string): Promise<void> {
    const row = findSaved(id);
    if (!row) {
      return;
    }
    const name = row.service.name;
    row.mode = "pendingDelete";
    row.errorKey = undefined;
    try {
      await serviceApi.deleteService(id);
      removeRow((r) => r.kind === "saved" && r.service.id === id);
      politeMessage.value = tr("services.a11y.deletedAnnounce", { name });
      toast.success(tr("services.a11y.deletedAnnounce", { name }));
    } catch (e) {
      const mapped = mapServiceError(e as AppError, "delete");
      if (mapped.removeRow) {
        removeRow((r) => r.kind === "saved" && r.service.id === id);
        toast.info(tr(mapped.i18nKey));
        return;
      }
      if (mapped.surface === "inline") {
        // 409 service_in_use → block (RESTRICT-ера, S7).
        row.mode = "blocked";
        row.errorKey = mapped.i18nKey;
      } else {
        row.mode = "deleteConfirm";
        toast.error(tr(mapped.i18nKey));
      }
    }
  }

  /** `POST /api/service-categories` → додати до categories. */
  async function createCategory(name: string): Promise<void> {
    const cat = await serviceCategoryApi.createCategory(name);
    categories.value.push(cat);
    toast.success(tr("services.categories.saved"));
  }

  /** `PATCH /api/service-categories/{id}` → оновити в place. Кидає далі — CategoryRow обробляє. */
  async function renameCategory(id: string, name: string): Promise<void> {
    const updated = await serviceCategoryApi.renameCategory(id, name);
    const idx = categories.value.findIndex((c) => c.id === id);
    if (idx !== -1) {
      categories.value[idx] = updated;
    }
    toast.success(tr("services.categories.saved"));
  }

  /**
   * `DELETE /api/service-categories/{id}` → прибрати з categories + обнулити categoryId
   * у рядках, що вказували на цю категорію (ADR-0025 D3: бекенд uncategorize-ить сервіси
   * у транзакції; ми синхронізуємо клієнтський стан, щоб послуги не зникали зі списку).
   * Кидає далі — CategoryRow обробляє.
   */
  async function removeCategory(id: string): Promise<void> {
    await serviceCategoryApi.removeCategory(id);
    categories.value = categories.value.filter((c) => c.id !== id);
    if (activeDraft.value?.categoryId === id) {
      closeDraft();
    }
    if (collapsedCategoryIds.value.has(id)) {
      const next = new Set(collapsedCategoryIds.value);
      next.delete(id);
      commitCollapsed(next);
    }
    // Синхронізуємо rows: послуги видаленої категорії → uncategorized (null).
    rows.value = rows.value.map((r) => {
      if (r.kind === "saved" && r.service.categoryId === id) {
        return { ...r, service: { ...r.service, categoryId: null } };
      }
      if (r.kind === "pendingCreate" && r.categoryId === id) {
        return { ...r, categoryId: null };
      }
      return r;
    });
    toast.success(tr("services.categories.deleted"));
  }

  return {
    listStatus,
    rows,
    categories,
    roster,
    rosterStatus,
    politeMessage,
    activeDraft,
    categoryDraftOpen,
    isEmpty,
    hasSavedRows,
    isFirstRun,
    categoryOptions,
    groups,
    mastersForService,
    assignedPreview,
    load,
    create,
    retryCreate,
    dismissCreate,
    beginRename,
    cancelRename,
    commitRename,
    beginDelete,
    cancelDelete,
    confirmDelete,
    createCategory,
    renameCategory,
    removeCategory,
    collapsedCategoryIds,
    toggleCategoryCollapse,
    isCategoryCollapsed,
    openDraft,
    closeDraft,
    isDraftOpen,
    openCategoryDraft,
    closeCategoryDraft,
  };
});
